import type { ApiResponse } from "../../../types/shared";
import httpClient from "../../../utils/httpClient";
import type {
  Agent,
  CreateAgentsParams,
  UpdateAgentParams,
  DeleteAgentParams,
  GenerateImageParams,
  GenerateImageResponse,
  ActivateAgentParams,
} from "../interfaces";

interface IAgentService {
  generateImage(params: GenerateImageParams): Promise<GenerateImageResponse>;
  createAgents(params: CreateAgentsParams): Promise<Agent[]>;
  getAllAgents(): Promise<Agent[]>;
  getAgentById(id: number): Promise<Agent>;
  activateAgent(params: ActivateAgentParams): Promise<Agent>;
  updateAgent(params: UpdateAgentParams): Promise<Agent>;
  deleteAgent(
    params: DeleteAgentParams
  ): Promise<{ message: string; id: number }>;
}

class AgentService implements IAgentService {
  private base = "/v1/agents";

  generateImage = async (
    params: GenerateImageParams
  ): Promise<GenerateImageResponse> => {
    const res = await httpClient.post<ApiResponse<GenerateImageResponse>>(
      `${this.base}/generate-image`,
      {
        prompt: params.prompt,
        ...(params.provider && { provider: params.provider }),
      }
    );

    return res.data.data;
  };

  createAgents = async (params: CreateAgentsParams): Promise<Agent[]> => {
    const res = await httpClient.post<ApiResponse<Agent[]>>(this.base, {
      agents: params.agents,
    });

    return res.data.data;
  };

  getAllAgents = async (): Promise<Agent[]> => {
    const res = await httpClient.get<ApiResponse<Agent[]>>(this.base);

    return res.data.data;
  };

  getAgentById = async (id: number): Promise<Agent> => {
    const res = await httpClient.get<ApiResponse<Agent>>(`${this.base}/${id}`);

    return res.data.data;
  };

  activateAgent = async (params: ActivateAgentParams): Promise<Agent> => {
    const res = await httpClient.put<ApiResponse<Agent>>(
      `${this.base}/activate`,
      {
        id: params.id,
        status: params.status,
      }
    );

    return res.data.data;
  };

  updateAgent = async (params: UpdateAgentParams): Promise<Agent> => {
    const res = await httpClient.put<ApiResponse<Agent>>(this.base, {
      id: params.id,
      ...(params.name && { name: params.name }),
      ...(params.attributes && { attributes: params.attributes }),
      ...(params.regenerateImage !== undefined && { regenerateImage: params.regenerateImage }),
    });

    return res.data.data;
  };

  deleteAgent = async (
    params: DeleteAgentParams
  ): Promise<{ message: string; id: number }> => {
    const res = await httpClient.delete<
      ApiResponse<{ message: string; id: number }>
    >(this.base, {
      data: { id: params.id },
    });

    return res.data.data;
  };
}

export const agentService = new AgentService();
